import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { formatSignedCurrencyUSD } from "@/lib/format";
import type { CohortRow } from "@/api/admin/economics";

const cardStyle: CSSProperties = {
  background: "var(--d-ink-2)",
  border: "1px solid var(--d-line)",
  borderRadius: 22,
  padding: 16,
  overflowX: "auto",
};

const tableStyle: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--d-fg-dim)",
  textAlign: "right",
  padding: "8px 12px 8px 0",
  borderBottom: "1px solid var(--d-line)",
};

const tdStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  fontVariantNumeric: "tabular-nums",
  color: "var(--d-fg)",
  padding: "10px 12px 10px 0",
  borderBottom: "1px solid var(--d-line)",
  textAlign: "right",
};

const emptyStyle: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 12,
  color: "var(--d-fg-dim)",
};

function netColor(n: number): string {
  if (n > 0) return "var(--d-gold)";
  if (n < 0) return "var(--d-ember)";
  return "var(--d-fg-muted)";
}

const HORIZONS = ["day_0", "day_7", "day_14", "day_30"] as const;

export function CohortTable({ rows, locale }: { rows: CohortRow[]; locale: string }): JSX.Element {
  const { t } = useTranslation("admin");
  if (rows.length === 0) {
    return (
      <div style={cardStyle}>
        <div style={emptyStyle}>{t("economics.cohorts.no_data")}</div>
      </div>
    );
  }
  return (
    <div style={cardStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: "left" }}>{t("economics.cohorts.header_week")}</th>
            <th style={thStyle}>{t("economics.cohorts.header_size")}</th>
            {HORIZONS.map((h) => (
              <th key={h} style={thStyle}>
                {t(`economics.cohorts.header_${h}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.cohort_week}>
              <td style={{ ...tdStyle, textAlign: "left" }}>{r.cohort_week}</td>
              <td style={tdStyle}>{r.cohort_size}</td>
              {HORIZONS.map((h) => {
                const net = r[h].net;
                return (
                  <td key={h} style={{ ...tdStyle, color: netColor(net) }}>
                    {formatSignedCurrencyUSD(net, locale)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
